import { Redirect, useHistory, useLocation } from 'react-router';
import { useCountryParam, useURLSearchParams } from 'utils/hooks';
import { Button } from '../components/Button';
import { Dialog } from '@headlessui/react';
import Modal from '../components/Modal';
import Stack from '../components/Stack';
import { UpgradeToSpateProLink } from '../components/NoAccessMessage';
import styled from 'styled-components';
import { useFetchCountries } from 'utils/hooks/api';
import { useFetchUserAbout } from 'utils/hooks/user';

const RedirectMessageHeader = styled(Stack).attrs(
    ({ as = 'header', $space = '.5rem' }) => ({
        as,
        $space,
    }),
)`
    && {
        border-bottom: 0;
    }
`;

function CurrentCountryNotAllowedMessage({ close }) {
    return (
        <>
            <RedirectMessageHeader>
                <Dialog.Title>Looking for something?</Dialog.Title>
                <Dialog.Description>
                    Book a meeting with your Spate Account Manager to gain
                    access to international markets.
                </Dialog.Description>
            </RedirectMessageHeader>
            <Stack as="footer" $direction="row" $space="6px">
                <Button intent="secondary" onClick={() => close()}>
                    Not Now
                </Button>
                <UpgradeToSpateProLink />
            </Stack>
        </>
    );
}

function CurrentCountryNotFoundMessage({ close }) {
    return (
        <>
            <RedirectMessageHeader>
                <Dialog.Title>Market Not Found</Dialog.Title>
                <Dialog.Description>
                    Could not find the requested market.
                </Dialog.Description>
            </RedirectMessageHeader>
            <Stack as="footer" $direction="row" $space="6px">
                <Button intent="primary" onClick={() => close()}>
                    Okay
                </Button>
            </Stack>
        </>
    );
}

const redirectReasons = {
    CURRENT_COUNTRY_NOT_ALLOWED: 'CURRENT_COUNTRY_NOT_ALLOWED',
    CURRENT_COUNTRY_NOT_FOUND: 'CURRENT_COUNTRY_NOT_FOUND',
};

const redirectMessages = {
    [redirectReasons.CURRENT_COUNTRY_NOT_ALLOWED]:
        CurrentCountryNotAllowedMessage,
    [redirectReasons.CURRENT_COUNTRY_NOT_FOUND]: CurrentCountryNotFoundMessage,
};

function MaybeRedirectGeo({ children }) {
    const history = useHistory();
    const location = useLocation();
    const { redirectReason, ...restState } = location.state || {};
    const searchParams = useURLSearchParams();
    const { currentCountryParam } = useCountryParam();
    const userAboutQuery = useFetchUserAbout();
    const countriesQuery = useFetchCountries();
    const currentCountry = countriesQuery.data?.find(
        ({ text_id }) => currentCountryParam === text_id,
    );
    const userAllowedCountries = userAboutQuery.data?.allowed_countries;
    const currentCountryNotAllowed =
        currentCountryParam &&
        userAllowedCountries &&
        !userAllowedCountries.includes(currentCountryParam);
    const currentCountryNotFound =
        currentCountryParam && !currentCountry && countriesQuery.data;
    const redirectTo = {
        ...location,
        search: `?${searchParams.toString()}`,
    };

    if (currentCountryNotAllowed) {
        searchParams.delete('country');
        redirectTo.state = {
            redirectReason: redirectReasons.CURRENT_COUNTRY_NOT_ALLOWED,
        };
        return <Redirect push to={redirectTo} />;
    }

    if (currentCountryNotFound) {
        searchParams.delete('country');
        redirectTo.state = {
            redirectReason: redirectReasons.CURRENT_COUNTRY_NOT_FOUND,
        };
        return <Redirect push to={redirectTo} />;
    }

    const Message = redirectMessages[redirectReason];
    const close = () =>
        history.push({
            ...location,
            state: { ...restState },
        });

    return (
        <>
            {children}
            <Modal open={!!redirectReason} onClose={close}>
                {Message ? <Message close={close} /> : null}
            </Modal>
        </>
    );
}

export default MaybeRedirectGeo;
